import { globalFetch } from "foundation/js/api"
import { mapBasketItemToGa4, trackRemoveFromCart } from "foundation/js/ecommerceTracking"

const _getMappedBasketItems = (json) => {
    if (!Array.isArray(json.cart.lineItems)) {
        throw new Error("Unable to parse data for basket");
    }

    var mappedArray = [];
    json.cart.lineItems.forEach((item) => {
        let mappedItem = _mapBasketItem(item);
        mappedArray.push(mappedItem);
    });
    return mappedArray;
};

const _mapBasketItem = (item) => {
    return {
        data: {
            title: item.displayName,
            productNumber: item.sku,
            imageUrl: item.imageUrl,
            errors: {
                hasErrors: item.validationErrors.length > 0 ? true : false,
                values: item.validationErrors,
            },
        },
        variantData: {
            minAmount: item.minQuantityPerOrder,
            maxAmount: item.maxQuantityPerOrder,
            variant: {
                quantity: item.quantity,
                displayName: item.displayName,
                sku: item.sku,
                price: {
                    displayPrice: item.price?.discountedPrice,
                    displayBeforePrice: item.price?.originalPrice,
                    priceAmount: item.price?.totalUnitPrice,
                    listPriceAmount: item.price?.originalUnitPrice
                },
                url: item.url,
            },
        },
        soldOut: !!item.validationErrors?.find(
            (item) => item.code === "ItemSoldOut",
        ),
        alreadyRemoved: false,
    };
};

const _updateBasketAndTotals = (context, json) => {
    let mappedArray = _getMappedBasketItems(json);
    context.commit("SET_BASKET", mappedArray);
    context.commit("SET_BASKET_TOTALS", json.totals);
    context.commit("SET_BASKET_ERRORS", json);
    context.commit("SET_BASKET_CONTAINS_UDLAANSVARE", json.containsUdlaansvare);
};

export default {
    namespaced: true,
    state: {
        basket: [],
        totals: null,
        basketState: {
            hasErrors: false,
            containsUdlaansvare: false,
        },
        recommendations: [],
    },
    mutations: {
        SET_BASKET_ERRORS(state, errors) {
            state.basketState.hasErrors = errors.hasValidationErrors;
        },
        SET_BASKET_CONTAINS_UDLAANSVARE(state, contains) {
            state.basketState.containsUdlaansvare = contains
        },
        SET_BASKET(state, basket) {
            state.basket = basket;
        },
        SET_BASKET_TOTALS(state, totals) {
            state.totals = {
                productsAmount: totals?.lineItemsCount ?? 0,
                productsPrice: totals?.subTotal,
                discount: totals?.discountTotal,
                VAT: totals?.taxTotal,
                total: totals?.total,
                shippingTotal: totals?.shippingTotal,
                isShippingFree: totals?.isShippingFree,
                areAllItemsFree: totals?.areAllItemsFree,
                isOrderFree: totals?.isOrderFree,
            };
        },
        SET_INITIAL_BASKET_TOTALS_PRODUCTS_AMOUNT(state, count) {
            const newTotals = Object.assign(
                {},
                state.totals ? state.totals : {},
                { productsAmount: count },
            );
            state.totals = newTotals;
        },
        set_recommendations(state, recommendations) {
            state.recommendations = recommendations;
        },
        clear_recommendations(state) {
            state.recommendations = [];
        }
    },
    getters: {},
    actions: {
        fetchBasket(context) {
            const url = `${document.location.origin}/${process.env.WEBSHOP_BASKET}`.replace('${pageId}', context.rootState.main.page_id);

            return globalFetch(url)
                .then((res) => {
                    if (res.ok) return res.json();
                    throw new Error("Unable to fetch basket");
                })
                .then((json) => {
                    _updateBasketAndTotals(context, json);
                })
                .catch((err) => console.error(err));
        },

        addToBasket(context, data) {
            if (!data?.quantity) {
                console.error("Unable to add item to basket; no data");
                return;
            }

            const url = `${document.location.origin}/${process.env.WEBSHOP_BASKET_LINE}`.replace('${pageId}', context.rootState.main.page_id);
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            const init = {
                headers,
                method: process.env.POSTMETHOD,
                body: JSON.stringify(data),
            };
            if (process.env.POSTMETHOD == "GET") delete init.body; // Workaround to handle dev calls

            return globalFetch(url, init)
                .then((res) => {
                    if (res.ok) return res.json();
                    throw new Error("Unable to add to  basket; " + res);
                })
                .then((json) => {
                    _updateBasketAndTotals(context, json);
                })
                .catch((err) => console.error(err));
        },

        adjustQuantity(context, data) {
            if (!data) {
                console.error("Unable to adjust item in basket; no data");
                return;
            }

            if (!data.newquantity) {
                data.newquantity = null;
            }

            const url = `${document.location.origin}/${process.env.WEBSHOP_BASKET_LINE}`.replace('${pageId}', context.rootState.main.page_id);
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            const init = {
                headers,
                method: process.env.PUTMETHOD,
                body: JSON.stringify(data),
            };
            if (process.env.POSTMETHOD == "GET") delete init.body; // Workaround to handle dev calls

            return globalFetch(url, init)
                .then((res) => {
                    if (res.ok) return res.json();
                    throw new Error("Unable to adjust quantity basket");
                })
                .then((json) => {
                    _updateBasketAndTotals(context, json);
                })
                .catch((err) => console.error(err));
        },

        removeItems(context, data) {
            if (!Array.isArray(data) || data.length == 0) {
                console.error(
                    "Unable to remove items from basket; invalid data",
                );
                return;
            }

            const skusToRemove = data.map(d => d.sku)
            const removedItems = context.state.basket
                .filter(item =>
                    skusToRemove.includes(item.variantData?.variant?.sku) ||
                    skusToRemove.includes(item.data?.productNumber)
                )
                .map((item, index) => mapBasketItemToGa4(item, index))

            const url = `${document.location.origin}/${process.env.WEBSHOP_BASKET_LINE}`.replace('${pageId}', context.rootState.main.page_id);
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            const init = {
                headers,
                method: process.env.DELETEMETHOD,
                body: JSON.stringify(data),
            };
            if (process.env.POSTMETHOD == "GET") delete init.body; // Workaround to handle dev calls

            return globalFetch(url, init)
                .then((res) => {
                    if (res.ok) return res.json();
                    throw new Error("Unable to delete item from basket");
                })
                .then((json) => {
                    _updateBasketAndTotals(context, json);
                    if (removedItems.length > 0) {
                        trackRemoveFromCart(removedItems)
                    }
                })
                .catch((err) => console.error(err));
        },

        updateActiveDeliveryType(context, payload) {
            const shippingMethod = (() => {
                switch (
                    payload // TODO czi: get from context.state.purchaseData
                ) {
                    case "home":
                        return "homeDelivery";
                    case "pickUp":
                        return "servicePointDelivery";
                    default:
                        throw new Error("No delivery type selected");
                }
            })();

            const url = `${document.location.origin}/${process.env.WEBSHOP_BASKET_SHIPPING}`.replace('${pageId}', context.rootState.main.page_id);
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            const init = {
                headers,
                method: process.env.PUTMETHOD,
                body: JSON.stringify(shippingMethod),
            };
            if (process.env.POSTMETHOD == "GET") delete init.body; // Workaround to handle dev calls

            globalFetch(url, init)
                .then((res) => {
                    if (res.status == 204)
                        // No content
                        return null;
                    if (res.ok) return res.json();
                    throw new Error("Unable to update shipping method");
                })
                .then((json) => {
                    if (!json) return; // No update
                    _updateBasketAndTotals(context, json);
                })
                .catch((err) => console.error(err));
        },

        async getRecommendations(context, data) {
            let recommendationUrl = `${document.location.origin}/${process.env.PRODUCT_RECOMMENDATIONS}`.replace('[productSku]', data.variantCode).replace('[take]', data.take);
            if (data.productCode) recommendationUrl += `&productCode=${encodeURIComponent(data.productCode)}`;
            return globalFetch(recommendationUrl)
                .then((res) => {
                    if (res.ok) return res.json();
                    throw new Error("Unable to fetch product recommendations");
                })
                .then((json) => {
                    context.commit('set_recommendations', json);
                })
                .catch((err) => console.error(err));
        }
    },
};
